import { useTrail, animated } from "@react-spring/web";
import { useGitCommits } from "@/hooks/git";
import type { GitCommit as GitCommitType } from "@/services/git/types";
import { ListSkeleton, EmptyList } from "./shared";

// ─── Commit List ──────────────────────────────────────────────────────────────

export function CommitList({
  repoFullName, defaultBranch, selectedSha, onSelect,
}: {
  repoFullName: string;
  defaultBranch: string | null | undefined;
  selectedSha: string | null;
  onSelect: (sha: string) => void;
}) {
  const { data: commits = [], isLoading, isError } = useGitCommits(repoFullName, defaultBranch);
  if (!defaultBranch) return (
    <EmptyList text="Select a working branch from the Branches tab to view commits." />
  );
  if (isLoading) return <ListSkeleton />;
  if (isError) return <EmptyList text="Could not load commits." />;
  if (commits.length === 0) return <EmptyList text="No commits found." />;
  return <CommitItems commits={commits} selectedSha={selectedSha} onSelect={onSelect} />;
}

function CommitItems({
  commits, selectedSha, onSelect,
}: {
  commits: GitCommitType[];
  selectedSha: string | null;
  onSelect: (sha: string) => void;
}) {
  const trail = useTrail(commits.length, {
    from: { opacity: 0, y: 4 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 34, clamp: true },
  });

  return (
    <>
      {trail.map((style, i) => {
        const c = commits[i];
        return (
          <animated.button
            key={c.sha}
            style={style}
            type="button"
            onClick={() => onSelect(c.sha)}
            className={[
              "w-full border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-0",
              c.sha === selectedSha ? "bg-primary/10" : "hover:bg-muted/30",
            ].join(" ")}
          >
            <p className="truncate text-xs font-medium leading-snug">
              {c.message.split("\n")[0]}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
              <span className="text-primary/80">{c.sha.slice(0, 7)}</span>
              <span>·</span>
              <span className="truncate">{c.author_name}</span>
              {c.committed_at && (
                <>
                  <span>·</span>
                  <span>{new Date(c.committed_at).toLocaleDateString()}</span>
                </>
              )}
            </p>
          </animated.button>
        );
      })}
    </>
  );
}
